import * as React from 'react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

export interface PillOption {
  code: string;
  labelZh: string;
  labelEn?: string;
}

export interface MultiSelectPillsProps {
  options: PillOption[];
  value: string[];
  onChange: (newValue: string[]) => void;
  columns?: number;
  disabled?: boolean;
  className?: string;
}

export const MultiSelectPills: React.FC<MultiSelectPillsProps> = ({
  options,
  value,
  onChange,
  columns,
  disabled = false,
  className,
}) => {
  const { i18n } = useTranslation();
  // Assume Chinese if language starts with zh (zh-HK, zh-TW, zh-CN, zh)
  const isZh = i18n.language?.toLowerCase().startsWith('zh');

  const handleToggle = (code: string) => {
    if (disabled) return;

    if (value.includes(code)) {
      onChange(value.filter((v) => v !== code));
    } else {
      onChange([...value, code]);
    }
  };

  return (
    <div
      className={cn(
        columns ? 'grid gap-2.5' : 'flex flex-wrap gap-2.5',
        columns === 1 && 'grid-cols-1',
        columns === 2 && 'grid-cols-2',
        columns === 3 && 'grid-cols-3',
        columns === 4 && 'grid-cols-4',
        columns === 5 && 'grid-cols-5',
        columns === 6 && 'grid-cols-6',
        className
      )}
    >
      {options.map((option) => {
        const active = value.includes(option.code);
        const label = isZh ? option.labelZh : (option.labelEn || option.labelZh);

        return (
          <button
            key={option.code}
            type="button"
            disabled={disabled}
            onClick={() => handleToggle(option.code)}
            className={cn(
              'rounded-full border px-4 py-2 text-sm font-medium transition duration-200',
              active
                ? 'border-primary bg-primary text-white shadow-[0_0_0_4px_rgba(45,106,92,0.14)]'
                : 'border-border bg-card text-card-foreground text-muted-foreground hover:border-primary/40 hover:text-primary',
              disabled && 'opacity-50 cursor-not-allowed hover:border-border hover:text-muted-foreground'
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
};
